import { Component, EventEmitter, forwardRef, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { MetaApiService } from 'src/app/services/meta-api.service';

/**
 * Selector de listas de mercado basado en ng-select.
 *
 * Reemplaza a los <select> nativos de "Mercados" y añade, a la izquierda de cada
 * opción del desplegable, un icono que abre la pantalla de edición de listas
 * (/listas?id=<idmercado>) en una pestaña nueva del navegador.
 *
 * Es un ControlValueAccessor, así que se usa como cualquier control:
 *   <app-mercado-select [mercados]="mercados" [(ngModel)]="selectedIdMercado"
 *                       (change)="changeInput('idMercado', $event)"></app-mercado-select>
 */
@Component({
  selector: 'app-mercado-select',
  templateUrl: './mercado-select.component.html',
  styleUrls: ['./mercado-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MercadoSelectComponent),
      multi: true,
    },
  ],
})
export class MercadoSelectComponent implements ControlValueAccessor, OnChanges, OnDestroy {
  /** Colección de mercados a mostrar (cada item con idmercado, iso, activos, metaapi). */
  @Input() mercados: any[] = [];
  @Input() disabled = false;
  @Input() clearable = false;
  @Input() searchable = true;
  @Input() placeholder = 'Mercado';
  /** Devuelve una etiqueta de cuenta atrás opcional para el idmercado dado (tablamercado). */
  @Input() countdownFn?: (idmercado: any) => string;
  /** Si es true, con sesión MetaApi iniciada las listas MetaTrader de otra cuenta se muestran
   *  desactivadas y avisan al pulsarlas. Sin sesión no se bloquea ninguna. */
  @Input() restringirCuentaMetaapi = false;

  /** Se emite al cambiar la selección, con la forma { value, target: { value } }
   *  para ser compatible con los handlers changeInput() existentes. */
  @Output() change = new EventEmitter<{ value: any; target: { value: any } }>();

  value: any = null;
  /** Copia de mercados con la marca disabled que entiende ng-select (no se toca el array de entrada). */
  itemsVista: any[] = [];

  private cuentaSub: Subscription;

  constructor(private metaApiService: MetaApiService, private toastr: ToastrService) {
    // Cambio de cuenta MetaApi en esta pestaña o en otra: recalcula los bloqueos y avisa si la
    // lista seleccionada deja de estar disponible (no se deselecciona para no disparar change).
    this.cuentaSub = this.metaApiService.cuentaCambiada$.subscribe(() => {
      this.refrescarItems();
      const seleccionada = this.seleccionada;
      if (seleccionada?.disabled) {
        this.toastr.warning(this.motivoBloqueo(seleccionada), 'Cuenta MetaTrader cambiada', { timeOut: 8000 });
      }
    });
  }

  ngOnChanges(): void {
    this.refrescarItems();
  }

  ngOnDestroy(): void {
    this.cuentaSub?.unsubscribe();
  }

  get seleccionada(): any {
    return this.itemsVista.find(m => m.idmercado === this.value);
  }

  /** Se llama también al abrir el desplegable, por si se cambió de cuenta MetaApi en el header. */
  refrescarItems(): void {
    this.itemsVista = (this.mercados || []).map(m => ({ ...m, disabled: this.bloqueada(m) }));
  }

  /** Solo se bloquea si la lista es de una cuenta y hay sesión iniciada con otra. Sin sesión se
   *  deja elegir: la pantalla ya ofrece "Conectar" para listas MetaTrader sin broker conectado. */
  bloqueada(mercado: any): boolean {
    if (!this.restringirCuentaMetaapi || !mercado?.metaapi || !mercado.metaapi_account_id) {
      return false;
    }
    const cuentaActiva = this.metaApiService.getAccountId();
    return !!cuentaActiva && mercado.metaapi_account_id !== cuentaActiva;
  }

  motivoBloqueo(mercado: any): string {
    return `La lista ${mercado.iso} pertenece a otra cuenta MetaTrader. Inicia sesión con esa cuenta para usarla.`;
  }

  avisarSiBloqueada(mercado: any): void {
    if (mercado?.disabled) {
      this.toastr.warning(this.motivoBloqueo(mercado), 'Lista no disponible');
    }
  }

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: any): void {
    this.value = value;
  }

  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onModelChange(value: any): void {
    this.value = value;
    this.onChange(value);
    this.onTouched();
    this.change.emit({ value, target: { value } });
  }

  label(mercado: any): string {
    if (!mercado) {
      return '';
    }
    const countdown = this.countdownFn ? this.countdownFn(mercado.idmercado) : '';
    const countdownPart = countdown ? ` - ${countdown}` : '';
    return `${mercado.iso}${countdownPart} (${mercado.activos})`;
  }

  /** Las listas con tipo 0 o 2 NO son editables; para el resto se muestra el icono. */
  esEditable(mercado: any): boolean {
    if (!mercado || mercado.idmercado == null) {
      return false;
    }
    const tipo = Number(mercado.tipo);
    return tipo !== 0 && tipo !== 2;
  }

  editarLista(mercado: any, event: MouseEvent): void {
    event.stopPropagation();
    event.preventDefault();
    if (this.esEditable(mercado)) {
      window.open(`/listas?id=${mercado.idmercado}`, '_blank');
    }
  }
}
